import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import { injectIntl, FormattedMessage } from 'react-intl'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import Loader from '@/checkout-instore-legacy/components/Commons/Loader'
import NoPaymentsImage from '@/checkout-instore-legacy/components/Commons/NoPaymentsImage'
import PaymentsPaidContainer from '@/checkout-instore-legacy/payment/containers/PaymentsPaidContainer'
import { CANCEL_PRINT_MESSAGES } from '@/checkout-instore-legacy/constants/PrintConstants'
import { getPaymentGroupProperties } from '@/checkout-instore-legacy/utils/PaymentUtils'
import {
  getPaymentsPaidFromOrder,
  getPaymentsFromOrder,
} from '@/checkout-instore-legacy/order/utils'
import { INPUT_TYPE } from '@/checkout-instore-legacy/constants/HookConstants'
import toastWebHookNotification from '@/checkout-instore-legacy/components/Commons/WebHook/toastWebHookNotification'

import InvoiceHook from './InvoiceHook'

const CancelOrderHeader = ({ orderGroup, orderGroupId, orderId }) => {
  const orderIds = orderId
    ? [orderId]
    : orderGroup
    ? orderGroup.map((order) => order.orderId)
    : []

  return (
    <div>
      {orderIds.map((cancelOrderId) => (
        <div
          id={`cancel-print-details-error-${cancelOrderId}`}
          data-testid={`cancel-print-details-error-${cancelOrderId}`}
          key={`cancel-print-details-error-${cancelOrderId}`}
          className="no-print print-details-error"
        />
      ))}
      {orderGroupId && (
        <h2 className="mv0 mt5 tc f3">
          <FormattedMessage id="orderGroupNumberLabel" /> {orderGroupId}
        </h2>
      )}
      {orderIds.map((cancelOrderId) => (
        <h3
          key={`cancel-order-number-${cancelOrderId}`}
          className="mv0 mt5 tc f4 fw4"
        >
          <FormattedMessage id="orderNumberLabel" /> {cancelOrderId}
        </h3>
      ))}
    </div>
  )
}

CancelOrderHeader.propTypes = {
  orderGroup: PropTypes.arrayOf(PropTypes.object).isRequired,
  orderGroupId: PropTypes.string,
  orderId: PropTypes.string,
}

const CancelOrderInvoiceHooks = ({ intl, device, orderGroup, orderId }) => {
  const cancelOrders = orderId
    ? orderGroup.filter((order) => order.orderId === orderId)
    : orderGroup

  return (
    <Fragment>
      {cancelOrders &&
        cancelOrders.map((cancelOrder) => (
          <InvoiceHook
            key={`cancelWebHook-${cancelOrder.orderId}`}
            sendImmediately
            type={INPUT_TYPE}
            order={cancelOrder}
            device={device}
            errorNodeSelector={`#cancel-print-details-error-${cancelOrder.orderId}`}
            messages={CANCEL_PRINT_MESSAGES(intl)}
          >
            {toastWebHookNotification}
          </InvoiceHook>
        ))}
    </Fragment>
  )
}

CancelOrderInvoiceHooks.propTypes = {
  orderGroup: PropTypes.arrayOf(PropTypes.object).isRequired,
  orderId: PropTypes.string,
  intl: intlShape.isRequired,
  device: PropTypes.shape({
    deviceId: PropTypes.string.isRequired,
    deviceName: PropTypes.string.isRequired,
  }),
}

class CancelOrder extends PureComponent {
  constructor(props) {
    super(props)
    const { orderId, orderGroup } = this.props.location.query
    this.state = {
      orderId: orderId ? orderId.trim() : null,
      orderGroupId: orderGroup ? orderGroup.trim() : null,
      loading: true,
    }
  }

  componentDidMount() {
    this.props.cancelGivenOrder({
      orderId: this.state.orderId,
      orderGroupId: this.state.orderGroupId,
    })
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.order.cancelOrder.orderGroup &&
      !prevProps.order.cancelOrder.orderGroup
    ) {
      getPaymentsPaidFromOrder(this.props.order.cancelOrder.orderGroup[0]).then(
        (paymentsPaid) => {
          this.props.updatePaymentsPaid(paymentsPaid)

          this.setState({
            loading: false,
          })
        }
      )
    }
  }

  componentWillUnmount() {
    this.props.clearCancelGivenOrder()
  }

  getFirstPaymentSystemName(payments) {
    const firstPayment = payments && payments[0]
    return firstPayment
      ? getPaymentGroupProperties(
          firstPayment.group,
          firstPayment.paymentSystemName
        ).label
      : ''
  }

  getPayments(order) {
    return getPaymentsFromOrder(order.cancelOrder.orderGroup[0]) || []
  }

  getPaymentsInfo(order) {
    if (
      !order ||
      !order.cancelOrder ||
      !order.cancelOrder.orderGroup ||
      order.cancelOrder.loading
    ) {
      return {}
    }

    const orderPayments = this.getPayments(order)
    return {
      orderGroupId:
        order.cancelOrder && order.cancelOrder.orderGroup
          ? order.cancelOrder.orderGroup[0].orderGroup
          : '',
      numberOfPayments: orderPayments.length,
      paymentSystemName: this.getFirstPaymentSystemName(orderPayments),
    }
  }

  render() {
    const { intl, order, device } = this.props
    const { loading } = this.state
    const paymentsPaid = order.paymentsPaid

    const noPayments = !paymentsPaid || paymentsPaid.length === 0
    const noPaymentsInfo = noPayments ? this.getPaymentsInfo(order) : {}

    return (
      <div className="flex flex-column vh-100">
        {loading ? (
          <Loader loading />
        ) : (
          <div className="flex-auto bg-base overflow-auto momentum relative">
            <CancelOrderHeader
              orderGroup={order.cancelOrder.orderGroup}
              orderGroupId={this.state.orderGroupId}
              orderId={this.state.orderId}
            />

            <CancelOrderInvoiceHooks
              orderGroup={order.cancelOrder.orderGroup}
              orderId={this.state.orderId}
              intl={intl}
              device={device}
            />

            {noPayments &&
              (order.cancelOrder.loading ? (
                <Loader loading />
              ) : (
                <div className="flex h-75 flex-column justify-center items-center">
                  <NoPaymentsImage />
                  <h2 className="c-on-base ma0 mt7">
                    <FormattedMessage id="noRefundPaymentsAvailableTitle" />
                  </h2>
                  <h3 className="normal c-muted-2 ma0 mt4">
                    <FormattedMessage
                      id={
                        order.cancelOrder.success
                          ? 'noRefundPaymentsAvailableSubtitle'
                          : 'noRefundPaymentsErrorSubtitle'
                      }
                      values={noPaymentsInfo}
                    />
                  </h3>
                </div>
              ))}

            <PaymentsPaidContainer />
          </div>
        )}
      </div>
    )
  }
}

CancelOrder.propTypes = {
  location: PropTypes.any,
  router: PropTypes.object,
  setSidebarVisibility: PropTypes.func,
  cancelGivenOrder: PropTypes.func.isRequired,
  clearCancelGivenOrder: PropTypes.func.isRequired,
  updatePaymentsPaid: PropTypes.func.isRequired,
  updateCancelFiscalCode: PropTypes.func.isRequired,
  order: PropTypes.shape({
    cancelOrder: PropTypes.object,
    paymentsPaid: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  intl: intlShape.isRequired,
  printingConfig: PropTypes.object,
  device: PropTypes.shape({
    deviceId: PropTypes.string.isRequired,
    deviceName: PropTypes.string.isRequired,
  }),
}

export default injectIntl(withRouter(CancelOrder))
